import {FilterFactory, InFilter, TermFilter} from '@dofleini/query-builder';
import {useUser} from '@dofleini/security';
import {proposalrequest, users} from '@mep/pae-commons';
import {useMemo} from 'react';
import {useQueries, useQuery} from 'react-query';
import {useLocation} from 'react-router-dom';

import {CACHE_KEY_LIST_USER, CACHE_KEY_USER, fields, USER_STATUS} from '@/modules/admin/contants/users';
import UserApiService from '@/modules/admin/services/UserApiService';
import {createFieldsHook} from '@/utils/createFieldsHook';
import {createGetOneHook} from '@/utils/createGetOneHook';

// eslint-disable-next-line no-unused-vars
const {SUBJECT_TYPE_ENUM} = proposalrequest;
const {USER_TYPES} = users;

export const useGetOneUser = createGetOneHook(
  UserApiService,
  'id',
  CACHE_KEY_USER,
);

export const useGetOneUserPublic = createGetOneHook(
  UserApiService.getOnePublic,
  'id',
  CACHE_KEY_USER,
);

const useFieldsBase = createFieldsHook('admin', fields);

export const useFields = (isViewMode, form, id) => {
  const fields = useFieldsBase(isViewMode);
  const {pathname} = useLocation();
  
  const isCreate = useMemo(() => pathname.endsWith('/create') && !id, [pathname, id]);
  const mappedFields = fields.filter(({key}) => {
    let keys = ['subjectType', 'lastProposal'];
    if (!isCreate) {
      keys = keys.concat(['password']);
    }
    return !keys.includes(key);
  });
  return useMemo(() => isCreate ? mappedFields.filter(({key}) => key !== 'status') : mappedFields, [mappedFields, isCreate]);
};

export const useUsersFields = (isViewMode, form, id, initValues = {}) => {
  const fields = useFieldsBase(isViewMode);
  const {pathname} = useLocation();
  
  const isCreate = useMemo(() => pathname.endsWith('/create') && !id, [pathname, id]);
  
  const isMain = useMemo(() => (initValues?.isRepresentative?.active || initValues?.isSoleOwner), [initValues?.isRepresentative?.active, initValues?.isSoleOwner]);
  
  const isUserRepresentative = useMemo(() => (USER_TYPES.USER === initValues?.userType) && isMain, [initValues?.userType, isMain]);
  
  let mappedFields = fields.filter(({key}) => {
    let keys = ['roles', 'userType', 'ips', 'level'];
    if (!isCreate) {
      keys = keys.concat(['security', 'password']);
    }
    return !keys.includes(key);
  });
  if (!isUserRepresentative)
    mappedFields = mappedFields.filter(({key}) => !['subjectType'].includes(key));
  
  return useMemo(() => isCreate ? mappedFields.filter(({key}) => key !== 'status') : mappedFields, [mappedFields, isCreate]);
};

export const useGetUsersByIds = (arrayId) => {
  const filters = new InFilter({field: '_id', value: arrayId});
  
  const queryConfig = useMemo(() => {
    return {enabled: arrayId && arrayId.length > 0};
  }, [arrayId]);
  
  return useQuery([CACHE_KEY_LIST_USER, filters], async () => {
    const {data} = await UserApiService.search({
      size: arrayId?.length,
      search: '',
      filters
    });
    return data;
  }, queryConfig);
};

export const useIsAdminByRoles = () => {
  const {isAuthenticated, hasRoles, hasPermission, user} = useUser();
  
  return useMemo(() => {
    if (!isAuthenticated || !user?.id) return false;
    return hasPermission('ADMIN') || hasRoles(['ADMIN', 'OFFICIAL']) || [
      USER_TYPES.FUNCTIONARY,
      USER_TYPES.FUNCTIONARY_ORGANIZATION,
      USER_TYPES.ONAT_USER,
      USER_TYPES.NOTARY,
      USER_TYPES.NOTARY_DPJ,
      USER_TYPES.COMMERCIAL_REGISTER
    ].includes(user?.userType);
  }, [hasPermission, hasRoles, isAuthenticated, user?.id, user?.userType]);
};

export const useIsBasicUser = () => {
  const {isAuthenticated, hasRoles, user} = useUser();
  if (!isAuthenticated) return false;
  return hasRoles('USER') && user?.userType === USER_TYPES?.USER;
};

export const useUsersByProposalTypes = (filters, search) => {
  const queries = SUBJECT_TYPE_ENUM?.map((type) => ({
    queryKey: [CACHE_KEY_LIST_USER, type, filters, search],
    queryFn: async () => {
      const {data} = await UserApiService.count({
        search,
        filters: FilterFactory.add(filters, new TermFilter({field: 'subjectType', value: type})).toQuery(),
      }
      );
      return {[type]: data?.total};
    },
    refetchOnWindowFocus: false
  }));
  
  const responses = useQueries(queries);
  
  return {
    data: responses.map((r) => {
      return r?.data;
    })?.reduce((r, e) => {
      return ({...r, ...e});
    }, {}),
    isLoading: responses.some(e => e?.isLoading)
  };
};

const removeFilter = (filters, filterToRemove) => {
  return FilterFactory.removeByFilter(filters, filterToRemove);
};

export const useUsersByStatus = (filters, search) => {
  const responses = useQueries([
    {
      queryKey: [CACHE_KEY_LIST_USER, USER_STATUS.UNVERIFIED, filters, search],
      queryFn: async () => {
        const {headers} = await UserApiService.search({
          search,
          page: 1,
          size: 1,
          filters: FilterFactory.add(
            FilterFactory.add(
              removeFilter(filters, new TermFilter({field: 'verified', value: false})),
              new TermFilter({field: 'verified', value: false})
            ),
            new TermFilter({field: 'userType', value: USER_TYPES.USER})
          ).toQuery(),
        });
        const total = Number(headers['x-total-count'] || 0);
        return {[USER_STATUS.UNVERIFIED]: total || 0};
      },
      refetchOnWindowFocus: false
    },
    {
      queryKey: [CACHE_KEY_LIST_USER, USER_STATUS.ACTIVE, filters, search],
      queryFn: async () => {
        const {headers} = await UserApiService.search({
          page: 1,
          size: 1,
          search,
          filters: FilterFactory.add(
            FilterFactory.add(
              removeFilter(filters, new TermFilter({field: 'verified', value: true})),
              new TermFilter({field: 'verified', value: true})
            ),
            new TermFilter({field: 'userType', value: USER_TYPES.USER})
          ).toQuery(),
        }
        );
        const total = Number(headers['x-total-count'] || 0);
        return {[USER_STATUS.ACTIVE]: total || 0};
      },
      refetchOnWindowFocus: false
    },
    {
      queryKey: [CACHE_KEY_LIST_USER, USER_STATUS.LOCK, filters, search],
      queryFn: async () => {
        const {headers} = await UserApiService.search({
          page: 1,
          size: 1,
          search,
          filters: FilterFactory.add(
            FilterFactory.add(
              filters, new TermFilter({field: 'lock', value: true})
            ),
            new TermFilter({field: 'userType', value: USER_TYPES.USER})
          ).toQuery(),
        }
        );
        const total = Number(headers['x-total-count'] || 0);
        return {[USER_STATUS.LOCK]: total || 0};
      },
      refetchOnWindowFocus: false
    },
  ]);
  
  return {
    data: responses.map((r) => {
      return r?.data;
    })?.reduce((r, e) => {
      return ({...r, ...e});
    }, {}),
    isLoading: responses.some(e => e?.isLoading)
  };
};

export const useUsersByFunctionariesTypes = (filters, search) => {
  const queries = [
    USER_TYPES.FUNCTIONARY,
    USER_TYPES.FUNCTIONARY_ORGANIZATION,
    USER_TYPES.ONAT_USER,
    USER_TYPES.NOTARY,
    USER_TYPES.NOTARY_DPJ,
    USER_TYPES.COMMERCIAL_REGISTER,
    USER_TYPES.HISTORIAN,
  ]?.map((type) => ({
    queryKey: [CACHE_KEY_LIST_USER, type, filters, search],
    queryFn: async () => {
      const {data} = await UserApiService.count({
        search,
        filters: FilterFactory.add(removeFilter(filters, new TermFilter({
          field: 'userType',
          value: type
        })), new TermFilter({field: 'userType', value: type})).toQuery(),
      }
      );
      return {[type]: data?.total};
    },
    refetchOnWindowFocus: false
  }));
  
  const responses = useQueries(queries);
  
  return {
    data: responses.map((r) => {
      return r?.data;
    })?.reduce((r, e) => {
      return ({...r, ...e});
    }, {}),
    isLoading: responses.some(e => e?.isLoading)
  };
};
